import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import type { SelectOption } from '@/components';
import type { BookingRefundDestination } from '@/types';

import { Button } from '@/components/ui/button';
import { DialogFooter } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';

import { cn } from '@/lib/utils';

import { bookingEventQueries } from '@/queries';

import {
  useCancelBookingReservationMutation,
  useMarkMissedBookingReservationMutation,
} from '@/mutations';

import { notifyBookingSuccess, notifyMutationError } from '@/helpers';

import { BookingDialogBody } from '../BookingDialogBody';
import { CancelBookingDialog } from '../ReservationDetailDialog';

import { AddRegistrationForm } from './AddRegistrationForm';
import { EventSummary } from './EventSummary';
import { RegistrationAttachForm } from './RegistrationAttachForm';
import { RegistrationsList } from './RegistrationsList';
import { ALL_WAVES, WavePicker } from './WavePicker';

type EventRegistrationsMode = 'view' | 'add' | 'attach';

export type EventRegistrationsProps = {
  eventId: number;
  eventsUrl: string;
  reservationsUrl: string;
  clientsSearchUrl: string;
  clientsUrl: string;
  newClientsUrl: string;
  cashRegistersUrl: string;
  refundReasons: SelectOption[];
  eventPageUrl?: string;
  initialWaveId?: number | null;
  onClose: () => void;
  onChanged: () => void;
};

export const EventRegistrations = (props: EventRegistrationsProps) => {
  const {
    eventId,
    eventsUrl,
    reservationsUrl,
    clientsSearchUrl,
    clientsUrl,
    newClientsUrl,
    cashRegistersUrl,
    refundReasons,
    eventPageUrl,
    initialWaveId = null,
    onChanged,
  } = props;
  const { t } = useTranslation();

  const [mode, setMode] = useState<EventRegistrationsMode>('view');
  const [selectedWaveId, setSelectedWaveId] = useState<number | null>(
    initialWaveId,
  );
  const [attachedRegistrationId, setAttachedRegistrationId] = useState<
    number | null
  >(null);
  const [cancelledRegistrationId, setCancelledRegistrationId] = useState<
    number | null
  >(null);
  const onBack = useCallback(() => {
    setMode('view');
    setAttachedRegistrationId(null);
  }, []);

  const {
    data: event,
    isLoading,
    refetch,
  } = useQuery({
    ...bookingEventQueries.detail({ url: eventsUrl, id: eventId }),
    refetchOnWindowFocus: true,
  });

  const onEventChange = useCallback(() => {
    refetch();
    onChanged();
  }, [onChanged, refetch]);

  const { mutate: markMissed, isPending: isMarkMissedPending } =
    useMarkMissedBookingReservationMutation();
  const { mutate: cancelRegistration, isPending: isCancelPending } =
    useCancelBookingReservationMutation();

  const isRegistrationActionPending = isMarkMissedPending || isCancelPending;

  const onAttend = useCallback((registrationId: number) => {
    setAttachedRegistrationId(registrationId);
    setMode('attach');
  }, []);

  const onMarkMissed = useCallback(
    (registrationId: number) => {
      markMissed(`${reservationsUrl}/${registrationId}`, {
        onSuccess: (response) => {
          notifyBookingSuccess(response);
          onEventChange();
        },
        onError: notifyMutationError,
      });
    },
    [markMissed, onEventChange, reservationsUrl],
  );

  const onCancelConfirm = useCallback(
    (data: BookingRefundDestination) => {
      if (!cancelledRegistrationId) {
        return;
      }
      cancelRegistration(
        { url: `${reservationsUrl}/${cancelledRegistrationId}`, data },
        {
          onSuccess: (response) => {
            notifyBookingSuccess(response);
            setCancelledRegistrationId(null);
            onEventChange();
          },
          onError: notifyMutationError,
        },
      );
    },
    [
      cancelRegistration,
      cancelledRegistrationId,
      onEventChange,
      reservationsUrl,
    ],
  );

  const waveNames = useMemo(
    () => new Map((event?.waves ?? []).map((wave) => [wave.id, wave.name])),
    [event?.waves],
  );

  const registrations = useMemo(() => {
    const rows = event?.registrations ?? [];

    if (!event?.split_into_waves || selectedWaveId === ALL_WAVES) {
      return rows;
    }

    return rows.filter((row) => row.event_wave_id === selectedWaveId);
  }, [event?.registrations, event?.split_into_waves, selectedWaveId]);

  const selectedWave = useMemo(
    () => event?.waves.find((wave) => wave.id === selectedWaveId),
    [event?.waves, selectedWaveId],
  );

  if (event && mode === 'add') {
    return (
      <AddRegistrationForm
        event={event}
        eventsUrl={eventsUrl}
        clientsSearchUrl={clientsSearchUrl}
        waveId={event.split_into_waves ? selectedWaveId : null}
        onClose={onBack}
        onSuccess={onEventChange}
      />
    );
  }

  if (mode === 'attach' && attachedRegistrationId) {
    return (
      <RegistrationAttachForm
        registrationId={attachedRegistrationId}
        reservationsUrl={reservationsUrl}
        clientsSearchUrl={clientsSearchUrl}
        clientsUrl={clientsUrl}
        newClientsUrl={newClientsUrl}
        onClose={onBack}
        onSuccess={onEventChange}
      />
    );
  }

  const isOpenForChanges =
    !!event && event.status === 'scheduled' && !event.completed;

  const freeSpots = event?.split_into_waves
    ? (selectedWave?.free_spots ?? null)
    : (event?.free_spots ?? null);

  const isWaveUnpicked = !!event?.split_into_waves && !selectedWave;
  const isAddDisabled = freeSpots === null || freeSpots <= 0;

  return (
    <>
      <BookingDialogBody>
        {isLoading && <Skeleton className="h-64 w-full" />}

        {!isLoading && event && (
          <>
            <EventSummary event={event} />

            {event.split_into_waves && (
              <>
                <Separator className="my-2" />

                <WavePicker
                  waves={event.waves}
                  selectedWaveId={selectedWaveId}
                  onWaveSelect={setSelectedWaveId}
                />
              </>
            )}

            <Separator className="my-2" />

            <p className="text-sm font-medium">{t('Registrations')}</p>

            <RegistrationsList
              registrations={registrations}
              waveNames={waveNames}
              isPending={isRegistrationActionPending}
              onAttend={onAttend}
              onMarkMissed={onMarkMissed}
              onCancel={setCancelledRegistrationId}
            />
          </>
        )}
      </BookingDialogBody>

      {/* Collapses instead of disappearing: its border and shadow are the body's bottom edge. */}
      <DialogFooter
        className={cn('flex-wrap gap-2', !isOpenForChanges && 'py-0!')}
      >
        {isOpenForChanges && (
          <>
            {isWaveUnpicked && (
              <span className="text-muted-foreground mr-auto text-sm">
                {t('Pick a wave to add a registration')}
              </span>
            )}

            {!!eventPageUrl && (
              <Button
                variant="outline"
                asChild
              >
                <a href={`${eventPageUrl}/${event.id}/edit`}>
                  {t('Edit event')}
                </a>
              </Button>
            )}

            <Button
              onClick={() => setMode('add')}
              disabled={isAddDisabled}
            >
              {t('Add registration')}
            </Button>
          </>
        )}
      </DialogFooter>

      {!!cancelledRegistrationId && (
        <CancelBookingDialog
          isOpen
          cashRegistersUrl={cashRegistersUrl}
          refundReasons={refundReasons}
          isPending={isCancelPending}
          title={t('Cancel this registration?')}
          description={t(
            'The place is freed. A purchase made for this registration is refunded.',
          )}
          onOpenChange={(isOpen) => !isOpen && setCancelledRegistrationId(null)}
          onConfirm={onCancelConfirm}
        />
      )}
    </>
  );
};
